import React, { useRef, useEffect, useState } from 'react';
import './LiquidMusicAnimation.scss';

const COLORS = [
  'rgba(26, 115, 232, ',   // blue
  'rgba(0, 191, 165, ',    // teal
  'rgba(30, 227, 207, ',   // cyan
];

function createBubble(width, height, randomY) {
  const r = 2 + Math.random() * 6;
  return {
    x: Math.random() * width,
    y: randomY ? Math.random() * height : height + r + Math.random() * 40,
    r,
    speed: 0.2 + Math.random() * 0.5,
    swayPhase: Math.random() * Math.PI * 2,
    swayAmp: 8 + Math.random() * 15,
    opacity: 0.15 + Math.random() * 0.3,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
  };
}

export default function LiquidMusicAnimation() {
  const canvasRef = useRef(null);
  const [prefersReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (prefersReducedMotion) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let bubbles = [];
    let raf;
    let lastTime = 0;
    let paused = false;

    function resize() {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      bubbles = [];
      for (let i = 0; i < 30; i++) {
        bubbles.push(createBubble(rect.width, rect.height, true));
      }
    }

    resize();
    window.addEventListener('resize', resize);

    const mql = window.matchMedia('(max-width: 600px)');
    const onMedia = (e) => { paused = e.matches; };
    onMedia(mql);
    mql.addEventListener('change', onMedia);

    function animate(ts) {
      raf = requestAnimationFrame(animate);
      if (paused) return;
      if (!lastTime) lastTime = ts;
      const dt = Math.min(ts - lastTime, 50);
      lastTime = ts;

      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);
      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        b.y -= b.speed * (dt / 16);
        b.swayPhase += 0.001 * dt;
        const drawX = b.x + Math.sin(b.swayPhase) * b.swayAmp;

        // Draw bubble
        ctx.beginPath();
        ctx.arc(drawX, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = b.color + b.opacity + ')';
        ctx.fill();

        // Thin border
        ctx.beginPath();
        ctx.arc(drawX, b.y, b.r, 0, Math.PI * 2);
        ctx.strokeStyle = b.color + (b.opacity * 0.6) + ')';
        ctx.lineWidth = 0.5;
        ctx.stroke();

        // Reset off-screen
        if (b.y < -b.r * 2) {
          bubbles[i] = createBubble(w, h, false);
        }
      }
    }

    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      mql.removeEventListener('change', onMedia);
    };
  }, [prefersReducedMotion]);

  if (prefersReducedMotion) return null;

  return (
    <div className="liquid-music-animation-container">
      <canvas ref={canvasRef} />
    </div>
  );
}
